import * as React from 'react';
import { useState, useEffect } from 'react';
import { Delete, UploadFile } from '@mui/icons-material';

import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { Box, FormLabel, IconButton, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import dayjs from 'dayjs';

import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// API calls
import { addAccount, updateAccount, uploadFile } from '../../api/account';

const initialState = {
  restaurantName: '',
  location: '',
  email: '',
  contactNo: '',
  website: '',
  expiryDate: null,
  logo: null,
  // Admin user details
  adminFirstName: '',
  adminLastName: '',
  adminEmail: '',
  adminContactNo: '',
};

export default function RestaurantDialog({
  dialogOpen,
  setDialogOpen,
  fetchData,
  selectedItem,
  action,
  setSnackbarMessage,
  setSnackbarOpen,
}) {
  const [inputs, setInputs] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const formData = new FormData();
  const getFileURL = async (file) => {
    formData.append('file', file);
    const { data } = await uploadFile(formData);
    return data.fileUrl;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('submit', inputs);
    setIsLoading(true);

    try {
      let logoUrl;
      if (file) {
        logoUrl = await getFileURL(file);
      }
      const { data } =
        action === 'CREATE'
          ? await addAccount({ ...inputs, logo: logoUrl })
          : await updateAccount(selectedItem._id, { ...inputs, logo: logoUrl });

      if (data.success) {
        fetchData();
        setSnackbarMessage({ msg: data.message, success: true });
        setSnackbarOpen(true);
        setDialogOpen(false);
      } else {
        setSnackbarMessage({ msg: data.message, success: false });
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage({ msg: error.message, success: false });
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setFilePreview(URL.createObjectURL(file));
    setFile(file);
  };
  const handleDiscardFile = (e) => {
    e.preventDefault();
    setFile(null);
    setFilePreview(null);
  };

  const handleInputChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const handleExpiryDate = (newValue) => {
    setInputs({ ...inputs, expiryDate: dayjs(newValue).format() });
  };

  useEffect(() => {
    console.log({ selectedItem });

    if (action !== 'CREATE') {
      setInputs({
        restaurantName: selectedItem.restaurantName,
        location: selectedItem.location,
        email: selectedItem.email,
        contactNo: selectedItem.contactNo,
        website: selectedItem.website,
        startTime: selectedItem.restaurantTiming.openTime,
        endTime: selectedItem.restaurantTiming.closeTime,
        expiryDate: dayjs(selectedItem.expiryDate),
        adminFirstName: selectedItem.adminDetails.firstName,
        adminLastName: selectedItem.adminDetails.lastName,
        adminEmail: selectedItem.adminDetails.email,
        adminContactNo: selectedItem.adminDetails.contactNo,
      });
    } else {
      setInputs(initialState);
      setLogoFile(null);
    }
  }, [dialogOpen]);

  return (
    <Box>
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        sx={{
          '& .MuiDialog-paper': {
            width: '600px',
          },
        }}
      >
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Typography variant="h6">{action === 'CREATE' ? 'Create' : 'Edit'} Account</Typography>
            <Stack spacing={2}>
              <TextField
                required
                margin="dense"
                name="restaurantName"
                label="Restaurant Name"
                type="text"
                fullWidth
                variant="standard"
                value={inputs.restaurantName}
                onChange={handleInputChange}
              />
              <TextField
                required
                margin="dense"
                name="location"
                label="Location"
                type="text"
                fullWidth
                variant="standard"
                value={inputs.location}
                onChange={handleInputChange}
              />
              <TextField
                required
                margin="dense"
                name="contactNo"
                label="Phone"
                inputProps={{ maxLength: 10 }}
                fullWidth
                variant="standard"
                value={inputs.contactNo}
                onChange={handleInputChange}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  required
                  margin="dense"
                  name="email"
                  label="Email"
                  type="email"
                  fullWidth
                  variant="standard"
                  value={inputs.email}
                  onChange={handleInputChange}
                />

                <TextField
                  required
                  margin="dense"
                  name="website"
                  label="Website"
                  type="text"
                  fullWidth
                  variant="standard"
                  value={inputs.website}
                  onChange={handleInputChange}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  required
                  margin="dense"
                  name="startTime"
                  label="Start Time"
                  fullWidth
                  variant="standard"
                  value={inputs.startTime}
                  onChange={handleInputChange}
                />

                <TextField
                  required
                  margin="dense"
                  name="endTime"
                  label="End Time"
                  fullWidth
                  variant="standard"
                  value={inputs.endTime}
                  onChange={handleInputChange}
                />
              </Box>

              {/* Admin User Details */}
              <Typography variant="subtitle1" sx={{ mt: 2 }}>Admin User Details</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  required
                  margin="dense"
                  name="adminFirstName"
                  label="Admin First Name"
                  fullWidth
                  variant="standard"
                  value={inputs.adminFirstName}
                  onChange={handleInputChange}
                />
                <TextField
                  required
                  margin="dense"
                  name="adminLastName"
                  label="Admin Last Name"
                  fullWidth
                  variant="standard"
                  value={inputs.adminLastName}
                  onChange={handleInputChange}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  required
                  margin="dense"
                  name="adminEmail"
                  label="Admin Email"
                  type="email"
                  fullWidth
                  variant="standard"
                  value={inputs.adminEmail}
                  onChange={handleInputChange}
                />
                <TextField
                  required
                  margin="dense"
                  name="adminContactNo"
                  label="Admin Contact No"
                  fullWidth
                  variant="standard"
                  value={inputs.adminContactNo}
                  onChange={handleInputChange}
                />
              </Box>

              {/* Expiry Date */}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  required
                  label="Expiry Date"
                  format="DD/MM/YYYY"
                  disablePast
                  value={inputs.expiryDate}
                  onChange={handleExpiryDate}
                />
              </LocalizationProvider>

              {/* Restaurant Logo Upload */}
              <Box
                sx={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
                component="form"
                onSubmit={handleSubmit}
              >
                <FormLabel sx={{ fontSize: '12px', color: '#344054', display: 'flex' }}>Restaurant Logo </FormLabel>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    width: '100%',
                    textAlign: 'center',
                    paddingBottom: '5px',
                  }}
                >
                  {!file && !filePreview ? (
                    <>
                      <IconButton sx={{ color: '#1976d2', fontSize: '30px' }} component="label">
                        <UploadFile />
                        <input
                          name="logoUrl"
                          type="file"
                          hidden
                          accept=".jpg, .jpeg, .png"
                          onChange={handleFileChange}
                        />
                      </IconButton>

                      <Typography fontSize={10} color="textSecondary">
                        JPEG, PNG File with Maximum size of 1 MB
                      </Typography>
                    </>
                  ) : (
                    <Box
                      py={1}
                      position="relative"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width={'100%'}
                    >
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                        }}
                        onClick={handleDiscardFile}
                      >
                        <Delete />
                      </IconButton>
                      <img src={filePreview} height={50} width={50} alt="logo" />
                    </Box>
                  )}
                </Box>
              </Box>
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} variant="contained">
              Cancel
            </Button>
            <LoadingButton loading={isLoading} type="submit" variant="contained">
              {action === 'CREATE' ? 'Create' : 'Update'}
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
